/**
 * Contact photo — the logic behind the mandatory crop step.
 *
 * The senior's phone shows each contact as a photo button (184 x 148 dp,
 * rounded corners, name underneath). Seniors have reduced vision, so the face
 * must fill that frame at a consistent size for every contact. That means the
 * caregiver never uploads the original photo: she crops it to the frame's
 * proportions and we save ONLY the crop, re-encoded at one fixed size.
 *
 * Orientation: browsers apply the photo's EXIF orientation when they decode
 * an <img> and when they draw it onto a canvas, and the re-encoded JPEG
 * carries no EXIF. So the saved image is upright whatever the camera wrote.
 *
 * Previews use data: URLs, not blob: URLs — the CSP in public/index.html
 * allows data: but not blob: for images.
 */

/** The phone's photo button, in dp (aidfone-android-2025 PhotoContactCard). */
export const FRAME = { width: 184, height: 148 } as const;
export const FRAME_ASPECT = FRAME.width / FRAME.height;

/** Saved size: 3x the frame, sharp on the densest (xxhdpi) phone screens. */
export const OUTPUT = { width: FRAME.width * 3, height: FRAME.height * 3 } as const;

/**
 * Smallest crop (in source pixels) we accept: 2x the frame. Below this the
 * photo is being stretched more than 1.5x on a typical senior phone and
 * looks soft. Tune here, nowhere else.
 */
export const MIN_SOURCE = { width: FRAME.width * 2, height: FRAME.height * 2 } as const;

/** The original is only read on the device, never uploaded — so allow big phone photos. */
export const MAX_INPUT_BYTES = 30 * 1024 * 1024;

export const OUTPUT_MIME = 'image/jpeg';
export const OUTPUT_QUALITY = 0.88;
export const OUTPUT_FILE_NAME = 'contact.jpg';

const ACCEPTED_MIME = ['image/jpeg', 'image/pjpeg', 'image/png', 'image/webp', 'image/heic', 'image/heif'];
const ACCEPTED_EXT = ['jpg', 'jpeg', 'png', 'webp', 'heic', 'heif'];

export type PhotoProblem =
  | 'unsupportedType'
  | 'tooLarge'
  | 'cannotOpen'
  | 'heicUnsupported'
  | 'tooZoomed'
  | 'tooSmall';

export interface Area {
  x: number;
  y: number;
  width: number;
  height: number;
}

function extensionOf(name: string): string {
  const dot = name.lastIndexOf('.');
  return dot === -1 ? '' : name.slice(dot + 1).toLowerCase();
}

export function isHeic(file: Pick<File, 'name' | 'type'>): boolean {
  const type = (file.type || '').toLowerCase();
  return type === 'image/heic' || type === 'image/heif' || ['heic', 'heif'].includes(extensionOf(file.name));
}

/** Checks the picked file before we try to open it. Returns null when it is fine. */
export function checkPickedFile(file: Pick<File, 'name' | 'type' | 'size'>): PhotoProblem | null {
  const type = (file.type || '').toLowerCase();
  // Some browsers report HEIC (and the odd JPEG) with an empty type; fall back to the extension.
  const accepted = type ? ACCEPTED_MIME.includes(type) : ACCEPTED_EXT.includes(extensionOf(file.name));
  if (!accepted) return 'unsupportedType';
  if (file.size > MAX_INPUT_BYTES) return 'tooLarge';
  return null;
}

/** Largest frame-shaped area that fits inside the image — the crop at zoom 1. */
export function largestCropFor(imageWidth: number, imageHeight: number): { width: number; height: number } {
  if (imageWidth / imageHeight > FRAME_ASPECT) {
    return { width: imageHeight * FRAME_ASPECT, height: imageHeight };
  }
  return { width: imageWidth, height: imageWidth / FRAME_ASPECT };
}

/** True when the whole photo is too small to ever give a sharp crop. */
export function isPhotoTooSmall(imageWidth: number, imageHeight: number): boolean {
  const largest = largestCropFor(imageWidth, imageHeight);
  // One pixel of slack: the crop component rounds to whole pixels.
  return largest.width < MIN_SOURCE.width - 1 || largest.height < MIN_SOURCE.height - 1;
}

/**
 * Quality gate for a crop. Returns null when the crop is sharp enough,
 * 'tooSmall' when no crop of this photo could be, 'tooZoomed' when zooming
 * out would fix it.
 */
export function checkCropQuality(
  area: Pick<Area, 'width' | 'height'>,
  image: { width: number; height: number },
): PhotoProblem | null {
  if (isPhotoTooSmall(image.width, image.height)) return 'tooSmall';
  if (area.width < MIN_SOURCE.width - 1 || area.height < MIN_SOURCE.height - 1) return 'tooZoomed';
  return null;
}

/**
 * CSS background that shows exactly the cropped area (given in percent of the
 * image, as the crop component reports it) inside a frame-shaped box. Used for
 * the live phone-button preview while cropping.
 */
export function previewBackground(areaPercent: Area): { backgroundSize: string; backgroundPosition: string } {
  const pos = (offset: number, size: number) => (size >= 100 ? 0 : (offset / (100 - size)) * 100);
  const round = (n: number) => Math.round(n * 1000) / 1000;
  return {
    backgroundSize: `${round(10000 / areaPercent.width)}% ${round(10000 / areaPercent.height)}%`,
    backgroundPosition: `${round(pos(areaPercent.x, areaPercent.width))}% ${round(pos(areaPercent.y, areaPercent.height))}%`,
  };
}

export interface DecodedPhoto {
  dataUrl: string;
  width: number;
  height: number;
  image: HTMLImageElement;
}

function readAsDataUrl(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(file);
  });
}

/**
 * Opens the photo on the device. HEIC opens only where the browser can decode
 * it (Safari on iPhone, iPad and Mac). Elsewhere the caregiver gets a clear
 * 'heicUnsupported' message rather than a broken crop.
 */
export async function decodePhoto(file: File): Promise<DecodedPhoto> {
  let dataUrl: string;
  try {
    dataUrl = await readAsDataUrl(file);
  } catch {
    throw new PhotoError('cannotOpen');
  }
  const image = new Image();
  try {
    await new Promise<void>((resolve, reject) => {
      image.onload = () => resolve();
      image.onerror = () => reject(new Error('decode failed'));
      image.src = dataUrl;
    });
  } catch {
    throw new PhotoError(isHeic(file) ? 'heicUnsupported' : 'cannotOpen');
  }
  if (!image.naturalWidth || !image.naturalHeight) {
    throw new PhotoError(isHeic(file) ? 'heicUnsupported' : 'cannotOpen');
  }
  return { dataUrl, width: image.naturalWidth, height: image.naturalHeight, image };
}

export class PhotoError extends Error {
  constructor(public readonly problem: PhotoProblem) {
    super(problem);
    this.name = 'PhotoError';
  }
}

function canvasOf(width: number, height: number): HTMLCanvasElement {
  const canvas = document.createElement('canvas');
  canvas.width = Math.max(1, Math.round(width));
  canvas.height = Math.max(1, Math.round(height));
  return canvas;
}

/**
 * Draws ONLY the cropped area at the fixed OUTPUT size and encodes it as JPEG.
 * Large photos are halved step by step first; a single big downscale aliases
 * on some browsers and makes faces look grainy.
 */
export function renderCrop(image: CanvasImageSource, area: Area): Promise<Blob> {
  let source: CanvasImageSource = image;
  let sx = area.x;
  let sy = area.y;
  let sw = area.width;
  let sh = area.height;

  while (sw / 2 >= OUTPUT.width && sh / 2 >= OUTPUT.height) {
    const step = canvasOf(sw / 2, sh / 2);
    const ctx = step.getContext('2d');
    if (!ctx) return Promise.reject(new Error('canvas unavailable'));
    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = 'high';
    ctx.drawImage(source, sx, sy, sw, sh, 0, 0, step.width, step.height);
    source = step;
    sx = 0;
    sy = 0;
    sw = step.width;
    sh = step.height;
  }

  const out = canvasOf(OUTPUT.width, OUTPUT.height);
  const ctx = out.getContext('2d');
  if (!ctx) return Promise.reject(new Error('canvas unavailable'));
  // White behind transparent PNGs, otherwise JPEG turns them black.
  ctx.fillStyle = '#ffffff';
  ctx.fillRect(0, 0, out.width, out.height);
  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = 'high';
  ctx.drawImage(source, sx, sy, sw, sh, 0, 0, out.width, out.height);

  return new Promise((resolve, reject) => {
    out.toBlob(
      (blob) => (blob ? resolve(blob) : reject(new Error('encode failed'))),
      OUTPUT_MIME,
      OUTPUT_QUALITY,
    );
  });
}

export function croppedFile(blob: Blob): File {
  return new File([blob], OUTPUT_FILE_NAME, { type: OUTPUT_MIME });
}

/**
 * Why an upload failed, in terms the caregiver can act on:
 * - 'uploadRefused': the server answered and said no (permission, size,
 *   type, server fault). Retrying will not help; she should contact support.
 * - 'uploadFailed': no answer at all — offline, timeout. Retrying may help.
 * supabase-js puts the HTTP status on StorageApiError only when a response
 * arrived; StorageUnknownError and fetch TypeErrors carry none.
 */
export function classifyUploadError(err: unknown): 'uploadRefused' | 'uploadFailed' {
  if (err && typeof err === 'object') {
    const { status, statusCode } = err as { status?: unknown; statusCode?: unknown };
    if (typeof status === 'number' && status >= 400) return 'uploadRefused';
    if (typeof statusCode === 'string' && /^[45]\d\d$/.test(statusCode)) return 'uploadRefused';
  }
  return 'uploadFailed';
}

// ── Sign-up: the crop stays on the device until the account exists ──────────
// Nobody may upload before signing in, and at the contacts step of sign-up the
// caregiver has no account yet. The crop is kept as a data: URL (the CSP allows
// data:, not blob:) and uploaded right after the account is created.

export function blobToDataUrl(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(blob);
  });
}

/** A crop kept on the device, not yet uploaded. Never a value to save to the database. */
export function isPendingPhoto(url: string | undefined | null): url is string {
  return typeof url === 'string' && url.startsWith('data:image/');
}

export function dataUrlToFile(dataUrl: string): File {
  const match = /^data:([^;,]+);base64,(.*)$/.exec(dataUrl);
  if (!match) throw new Error('not a base64 data URL');
  const binary = atob(match[2]);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
  return new File([bytes], OUTPUT_FILE_NAME, { type: match[1] });
}
